import { useNavigate } from 'react-router-dom'
import {
  Sparkles, Shield, Share2, FileText, Palette,
  Zap, Check, Star, ArrowRight, ChevronDown, Users, BarChart3,
  Clock, Globe, Lock, PenTool, Send, Eye,
  FileSignature, MousePointerClick
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useLocale } from '../contexts/LocaleContext'

// ─── Animated counter ───
function AnimatedNumber({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  useEffect(() => {
    const duration = 2000
    const steps = 60
    const increment = target / steps
    let current = 0
    const timer = setInterval(() => {
      current += increment
      if (current >= target) {
        setCount(target)
        clearInterval(timer)
      } else {
        setCount(Math.floor(current))
      }
    }, duration / steps)
    return () => clearInterval(timer)
  }, [target])
  return <>{count.toLocaleString()}{suffix}</>
}

// ─── FAQ Accordion ───
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm hover:border-slate-500/30 transition-all">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
      >
        <span className="font-medium text-white text-lg pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="px-6 pb-6 text-slate-300 leading-relaxed">
            {answer}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Floating badge component ───
function FloatingBadge({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`absolute bg-white/95 backdrop-blur-sm shadow-2xl shadow-black/10 rounded-2xl px-4 py-3 border border-white/50 ${className}`}>
      {children}
    </div>
  )
}

export default function LandingPage() {
  const navigate = useNavigate()
  const { tx } = useLocale()

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: tx('AI Writes It For You', 'ה-AI כותב עבורך'),
      description: tx('No more staring at a blank page. Describe your project in one line — AI delivers a complete, professional proposal in under 30 seconds.', 'לא עוד מבט בדף ריק. תאר את הפרויקט בשורה אחת — ה-AI מגיש הצעה מקצועית ומלאה תוך פחות מ-30 שניות.'),
      visual: 'ai'
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: tx('Build Any Proposal, Visually', 'בנה כל הצעה, ויזואלית'),
      description: tx('22+ drag-and-drop blocks — price tables, timelines, galleries, and more. Your proposals look premium without any design skills.', 'יותר מ-22 בלוקים בגרירה ושחרור — טבלאות מחירים, לוחות זמנים, גלריות ועוד. ההצעות שלך נראות מקצועיות ללא כישורי עיצוב.'),
      visual: 'editor'
    },
    {
      icon: <FileSignature className="w-6 h-6" />,
      title: tx('Get Signed — Instantly', 'קבל חתימה — מיידית'),
      description: tx('No printing, no scanning, no chasing. Clients sign from any device in seconds. Legally binding, timestamped, and stored automatically.', 'ללא הדפסה, סריקה או מרדפים. לקוחות חותמים מכל מכשיר תוך שניות. תקף משפטית, עם חותמת זמן ואחסון אוטומטי.'),
      visual: 'signature'
    },
    {
      icon: <Share2 className="w-6 h-6" />,
      title: tx('Share in One Click', 'שתף בקליק אחד'),
      description: tx('Send a link — your client opens a beautiful, interactive proposal right in their browser. No app download, no login required.', 'שלח קישור — הלקוח פותח הצעה יפה ואינטראקטיבית ישירות בדפדפן שלו. ללא הורדת אפליקציה, ללא כניסה נדרשת.'),
      visual: 'share'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: tx('Know Who\'s Ready to Sign', 'דע מי מוכן לחתום'),
      description: tx('See exactly when a client opens your proposal and how long they spend on each section. Follow up at the perfect moment — never guess again.', 'ראה בדיוק מתי לקוח פתח את ההצעה וכמה זמן בילה בכל סעיף. עקוב אחריו ברגע הנכון — לעולם אל תנחש שוב.'),
      visual: 'analytics'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: tx('PDF in One Click', 'PDF בקליק אחד'),
      description: tx('Need to send a traditional PDF? Export a polished, print-ready version instantly — no reformatting, no extra tools.', 'צריך לשלוח PDF מסורתי? ייצא גרסה מעוצבת ומוכנה להדפסה מיידית — ללא עיצוב מחדש, ללא כלים נוספים.'),
      visual: 'pdf'
    },
  ]

  const steps = [
    {
      icon: <PenTool className="w-7 h-7" />,
      title: tx('Create', 'צור'),
      description: tx('Use AI or start from a template. Describe what you need and get a professional proposal instantly.', 'השתמש בבינה מלאכותית או התחל מתבנית. תאר מה אתה צריך וקבל הצעה מקצועית מיידית.'),
      color: 'from-slate-500 to-accent-500'
    },
    {
      icon: <Send className="w-7 h-7" />,
      title: tx('Send', 'שלח'),
      description: tx('Share a unique link with your client. They can view your proposal from any device.', 'שתף קישור ייחודי עם הלקוח שלך. הוא יכול לצפות בהצעה מכל מכשיר.'),
      color: 'from-accent-500 to-orange-400'
    },
    {
      icon: <MousePointerClick className="w-7 h-7" />,
      title: tx('Close', 'סגור'),
      description: tx('Your client signs digitally. You get notified instantly. The deal is done.', 'הלקוח שלך חותם דיגיטלית. אתה מקבל התראה מיידית. העסקה נסגרה.'),
      color: 'from-orange-400 to-amber-400'
    }
  ]

  const testimonials = [
    {
      name: tx('Sarah Mitchell', 'שרה מיטשל'),
      role: tx('Marketing Agency Owner', 'בעלת סוכנות שיווק'),
      text: tx('We used to spend 3–4 hours crafting each proposal in Word. With Quor, we\'re done in 15 minutes — and our close rate jumped 40% in the first month.', 'נהגנו לבזבז 3-4 שעות על כל הצעה ב-Word. עם Quor, אנחנו מסיימים תוך 15 דקות — ושיעור הסגירה שלנו קפץ ב-40% בחודש הראשון.'),
      avatar: 'S'
    },
    {
      name: tx('David Chen', 'דוד חן'),
      role: tx('Freelance Developer', 'מפתח פרילנסר'),
      text: tx('I landed a $24K project because my proposal looked more professional than every competitor\'s. Quor made that possible without me being a designer.', 'זכיתי בפרויקט של $24K כי ההצעה שלי נראתה מקצועית יותר מהמתחרים. Quor הפך את זה לאפשרי בלי שאהיה מעצב.'),
      avatar: 'D'
    },
    {
      name: tx('Emma Rodriguez', 'אמה רודריגז'),
      role: tx('Interior Design Studio', 'סטודיו לעיצוב פנים'),
      text: tx('Clients used to ghost me after receiving a PDF. Now they open the Quor link, sign the same day, and comment on how smooth the experience was.', 'לקוחות נהגו להיעלם לי אחר שקיבלו PDF. עכשיו הם פותחים את הקישור של Quor, חותמים באותו יום, ומעירים כמה החוויה חלקה.'),
      avatar: 'E'
    }
  ]

  const faqs = [
    {
      question: tx('Are digital signatures legally binding?', 'האם חתימות דיגיטליות תקפות משפטית?'),
      answer: tx('Yes. Digital signatures created through Quor are recognized under electronic signature laws worldwide. Each signature includes a timestamp, IP address, and signer identity verification.', 'כן. חתימות דיגיטליות שנוצרו דרך Quor מוכרות על פי חוקי חתימה אלקטרונית ברחבי העולם. כל חתימה כוללת חותמת זמן, כתובת IP ואימות זהות החותם.')
    },
    {
      question: tx('What happens after my free plan runs out?', 'מה קורה אחרי שהתוכנית החינמית נגמרת?'),
      answer: tx('The free plan includes 3 proposals — forever. When you need more, upgrade to Pro ($12/mo) for 20 proposals or Business ($39/mo) for unlimited. Upgrade or cancel anytime, no commitments.', 'התוכנית החינמית כוללת 3 הצעות — לתמיד. כשאתה צריך יותר, שדרג ל-Pro ($12 לחודש) ל-20 הצעות או Business ($39 לחודש) ללא הגבלה. שדרג או בטל בכל עת, ללא התחייבויות.')
    },
    {
      question: tx('How does the AI proposal generation work?', 'איך עובדת יצירת ההצעות עם בינה מלאכותית?'),
      answer: tx('Simply describe your project in one sentence (e.g., "Website redesign for a boutique hotel"). Our AI generates a complete proposal with relevant sections, professional terms, and pricing structure — ready to customize and send.', 'פשוט תאר את הפרויקט שלך במשפט אחד (למשל, "עיצוב מחדש של אתר עבור מלון בוטיק"). הבינה המלאכותית שלנו יוצרת הצעה מלאה עם סעיפים רלוונטיים, תנאים מקצועיים ומבנה תמחור — מוכנה להתאמה אישית ושליחה.')
    },
    {
      question: tx('Is my data secure?', 'האם המידע שלי מאובטח?'),
      answer: tx('Absolutely. We use industry-standard encryption, secure cloud storage with row-level security, and OAuth authentication through Google. Your data and your clients\' data are always protected.', 'בהחלט. אנחנו משתמשים בהצפנה בתקן תעשייתי, אחסון ענן מאובטח עם אבטחה ברמת שורה, ואימות OAuth דרך Google. המידע שלך והמידע של הלקוחות שלך תמיד מוגנים.')
    },
    {
      question: tx('Can I customize the look of my proposals?', 'האם אני יכול להתאים אישית את המראה של ההצעות שלי?'),
      answer: tx('Yes! Choose your brand colors, upload your logo, select from 22+ block types, and arrange everything exactly how you want with our drag-and-drop editor. Every proposal can match your brand identity.', 'כן! בחר את צבעי המותג שלך, העלה את הלוגו שלך, בחר מתוך יותר מ-22 סוגי בלוקים, וסדר הכל בדיוק כפי שאתה רוצה עם העורך שלנו בגרירה ושחרור. כל הצעה יכולה להתאים לזהות המותג שלך.')
    }
  ]

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative overflow-hidden" dir="ltr">
      {/* ─── Ambient background ─── */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-950/40 via-[#0a0a0f] to-[#0a0a0f]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-slate-500/[0.07] rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-rose-500/[0.05] rounded-full blur-[100px]" />
      </div>

      {/* ─── Content ─── */}
      <div className="relative z-10">

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* NAVBAR                                                      */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-[#0a0a0f]/80 border-b border-white/[0.06] pt-safe">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <img src="/media/quor_icon.png" alt="QUOR - Digital Signatures Made Simple" className="h-10" />

            <div className="hidden md:flex items-center gap-8 text-[13px] text-slate-400">
              <a href="#features" className="hover:text-white transition-colors duration-200">{tx('Features', 'תכונות')}</a>
              <a href="#how-it-works" className="hover:text-white transition-colors duration-200">{tx('How It Works', 'איך זה עובד')}</a>
              <a href="#pricing" className="hover:text-white transition-colors duration-200">{tx('Pricing', 'מחירים')}</a>
              <a href="#faq" className="hover:text-white transition-colors duration-200">{tx('FAQ', 'שאלות נפוצות')}</a>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/login')}
                className="text-sm text-slate-300 hover:text-white transition-colors px-4 py-2"
              >
                {tx('Sign In', 'התחברות')}
              </button>
              <button
                onClick={() => navigate('/login?signup=1')}
                className="text-sm bg-accent-500 text-white font-semibold px-5 py-2.5 rounded-full hover:bg-accent-600 transition-all duration-200 shadow-lg shadow-accent-500/25"
              >
                {tx('Get Started Free', 'התחל בחינם')}
              </button>
            </div>
          </div>
        </nav>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* HERO                                                        */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Full logo */}
            <div className="mb-10">
              <img
                src="/media/quor_full_white.png"
                alt="Quor — Agreements. Contracts. Signatures."
                className="h-32 sm:h-44 mx-auto"
              />
            </div>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/[0.08] rounded-full px-5 py-2 mb-8">
              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-sm text-slate-300">{tx('Businesses close deals 3x faster with Quor', 'עסקים סוגרים עסקאות מהר פי 3 עם Quor')}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-white leading-[1.15] tracking-tight mb-6">
              {tx('Stop losing deals to', 'הפסק לאבד עסקאות בגלל')}
              <br />
              <span className="bg-gradient-to-r from-slate-400 via-accent-400 to-orange-300 bg-clip-text text-transparent">
                {tx('slow, messy proposals', 'הצעות מחיר איטיות ומבולגנות')}
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              {tx('Write a one-line brief — Quor\'s AI builds a polished, client-ready proposal in seconds. Share a link, get a digital signature, close the deal. No back-and-forth. No PDFs emailed back and forth.', 'כתוב תיאור קצר — ה-AI של Quor בונה הצעה מקצועית ומוכנה ללקוח תוך שניות. שתף קישור, קבל חתימה דיגיטלית, סגור את העסקה. ללא התכתבויות אין סופיות.')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <button
                onClick={() => navigate('/login?signup=1')}
                className="group w-full sm:w-auto flex items-center justify-center gap-2 bg-accent-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-accent-600 hover:shadow-lg hover:shadow-accent-500/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                <span>{tx('Send My First Proposal Free', 'שלח את ההצעה הראשונה שלי בחינם')}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full sm:w-auto text-slate-300 hover:text-white px-8 py-4 rounded-full font-medium text-lg border border-white/10 hover:border-white/20 hover:bg-white/[0.03] transition-all duration-200"
              >
                {tx('See It in Action', 'ראה את זה בפעולה')}
              </button>
            </div>

            {/* Stats row */}
            <div className="flex items-center justify-center gap-8 sm:gap-12 mt-12 flex-wrap">
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white"><AnimatedNumber target={1200} suffix="+" /></div>
                <div className="text-xs text-slate-500 mt-1">{tx('Businesses Using Quor', 'עסקים שמשתמשים ב-Quor')}</div>
              </div>
              <div className="w-px h-8 bg-white/10 hidden sm:block" />
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white"><AnimatedNumber target={10000} suffix="+" /></div>
                <div className="text-xs text-slate-500 mt-1">{tx('Proposals Sent & Signed', 'הצעות שנשלחו ונחתמו')}</div>
              </div>
              <div className="w-px h-8 bg-white/10 hidden sm:block" />
              <div className="text-center">
                <div className="flex items-center gap-1 justify-center">
                  <span className="text-2xl sm:text-3xl font-bold text-white">4.9</span>
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                </div>
                <div className="text-xs text-slate-500 mt-1">{tx('Average User Rating', 'דירוג ממוצע של משתמשים')}</div>
              </div>
              <div className="w-px h-8 bg-white/10 hidden sm:block" />
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white">~30<span className="text-lg font-semibold">s</span></div>
                <div className="text-xs text-slate-500 mt-1">{tx('Avg. Time to First Proposal', 'זמן ממוצע להצעה הראשונה')}</div>
              </div>
            </div>
          </div>

          {/* ─── Hero Product Mockup ─── */}
          <div className="mt-20 max-w-5xl mx-auto">
            <div className="relative">
              {/* Glow behind */}
              <div className="absolute -inset-4 bg-gradient-to-r from-slate-500/20 via-accent-500/10 to-orange-500/20 rounded-3xl blur-2xl" />

              <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] shadow-2xl">
                {/* Browser chrome */}
                <div className="bg-[#1a1a24] px-4 py-3 flex items-center gap-3 border-b border-white/[0.06]">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                    <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                    <div className="w-3 h-3 rounded-full bg-[#28c840]" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="bg-white/[0.06] rounded-lg px-6 py-1.5 text-xs text-slate-500 flex items-center gap-2">
                      <Lock className="w-3 h-3 text-emerald-400" />
                      quor.app/dashboard
                    </div>
                  </div>
                </div>

                {/* App mockup - Dashboard view */}
                <div className="bg-[#f8f9fb] p-0">
                  <div className="flex">
                    {/* Sidebar mockup */}
                    <div className="hidden sm:flex w-16 bg-white border-r border-slate-200 flex-col items-center py-4 gap-4 flex-shrink-0">
                      <div className="w-9 h-9 rounded-lg bg-accent-500 flex items-center justify-center">
                        <span className="text-white font-bold text-sm">Q</span>
                      </div>
                      <div className="space-y-3 mt-2">
                        <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center"><BarChart3 className="w-4 h-4 text-slate-500" /></div>
                        <div className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center"><FileText className="w-4 h-4 text-slate-400" /></div>
                        <div className="w-8 h-8 rounded-lg hover:bg-slate-50 flex items-center justify-center"><Users className="w-4 h-4 text-slate-400" /></div>
                      </div>
                    </div>

                    {/* Main content */}
                    <div className="flex-1 p-4 sm:p-6">
                      {/* Top bar */}
                      <div className="flex items-center justify-between mb-5">
                        <div>
                          <h3 className="text-base font-semibold text-slate-800">{tx('Dashboard', 'לוח בקרה')}</h3>
                          <p className="text-xs text-slate-400 mt-0.5">{tx('Welcome back, here\'s your overview', 'ברוך שובך, הנה הסקירה שלך')}</p>
                        </div>
                        <button className="bg-accent-500 text-white text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-1.5 shadow-sm">
                          <Sparkles className="w-3.5 h-3.5" /> {tx('New Proposal', 'הצעה חדשה')}
                        </button>
                      </div>

                      {/* Stat cards */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                        {[
                          { label: tx('Total Proposals', 'סה"כ הצעות'), value: '24', change: '+18%', up: true },
                          { label: tx('Signed', 'נחתמו'), value: '19', change: '+12%', up: true },
                          { label: tx('Conversion', 'המרה'), value: '85%', change: '+5%', up: true },
                          { label: tx('Revenue', 'הכנסות'), value: '$45.2K', change: '+22%', up: true },
                        ].map((stat, i) => (
                          <div key={i} className="bg-white rounded-xl p-3 border border-slate-100">
                            <div className="text-[11px] text-slate-400 mb-1">{stat.label}</div>
                            <div className="text-lg font-bold text-slate-800">{stat.value}</div>
                            <div className="text-[10px] text-emerald-500 font-medium mt-0.5">{stat.change} {tx('this month', 'החודש')}</div>
                          </div>
                        ))}
                      </div>

                      {/* Proposal list */}
                      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-600">{tx('Recent Proposals', 'הצעות אחרונות')}</span>
                          <span className="text-[10px] text-slate-500 font-medium cursor-pointer">{tx('View All', 'צפה בכל')} →</span>
                        </div>
                        {[
                          { name: tx('Website Redesign — Acme Corp', 'עיצוב מחדש אתר — Acme Corp'), status: tx('Signed', 'נחתם'), color: 'bg-emerald-50 text-emerald-600', amount: '$12,500', date: tx('Feb 12', '12 בפבר') },
                          { name: tx('Marketing Strategy — StartupXYZ', 'אסטרטגיית שיווק — StartupXYZ'), status: tx('Viewed', 'נצפה'), color: 'bg-blue-50 text-blue-600', amount: '$8,900', date: tx('Feb 10', '10 בפבר') },
                          { name: tx('Brand Identity — Luxe Hotels', 'זהות מותגית — Luxe Hotels'), status: tx('Sent', 'נשלח'), color: 'bg-amber-50 text-amber-600', amount: '$6,200', date: tx('Feb 8', '8 בפבר') },
                          { name: tx('App Development — FinTech Co', 'פיתוח אפליקציה — FinTech Co'), status: tx('Draft', 'טיוטה'), color: 'bg-slate-50 text-slate-500', amount: '$24,000', date: tx('Feb 6', '6 בפבר') },
                        ].map((row, i) => (
                          <div key={i} className={`px-4 py-2.5 flex items-center justify-between ${i < 3 ? 'border-b border-slate-50' : ''} hover:bg-slate-50/50`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-7 h-7 rounded-lg bg-gradient-to-br bg-accent-100 flex items-center justify-center flex-shrink-0">
                                <FileText className="w-3.5 h-3.5 text-slate-500" />
                              </div>
                              <span className="text-xs text-slate-700 truncate">{row.name}</span>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                              <span className="text-[10px] text-slate-400 hidden sm:block">{row.date}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${row.color}`}>{row.status}</span>
                              <span className="text-xs font-semibold text-slate-700 w-16 text-right">{row.amount}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <FloatingBadge className="hidden lg:flex items-center gap-3 -left-8 top-1/3 animate-[float_6s_ease-in-out_infinite]">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-800">{tx('Proposal Signed!', 'ההצעה נחתמה!')}</div>
                  <div className="text-[10px] text-slate-400">Acme Corp — $12,500</div>
                </div>
              </FloatingBadge>

              <FloatingBadge className="hidden lg:flex items-center gap-3 -right-6 top-2/3 animate-[float_6s_ease-in-out_infinite_2s]">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-slate-700" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-800">{tx('Client Viewed', 'הלקוח צפה')}</div>
                  <div className="text-[10px] text-slate-400">{tx('Just now — Marketing Strategy', 'עכשיו — אסטרטגיית שיווק')}</div>
                </div>
              </FloatingBadge>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* LOGO STRIP / SOCIAL PROOF                                   */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-center text-xs text-slate-600 uppercase tracking-widest mb-8">{tx('Used daily by freelancers, agencies, studios & startups worldwide', 'בשימוש יומיומי על ידי פרילנסרים, סוכנויות, סטודיוס וסטארטאפים ברחבי העולם')}</p>
          <div className="flex items-center justify-center gap-8 sm:gap-16 flex-wrap opacity-40">
            {[
              tx('Agency', 'סוכנות'),
              tx('Startup', 'סטארטאפ'),
              tx('Studio', 'סטודיו'),
              tx('Freelance', 'פרילנס'),
              tx('Enterprise', 'אנטרפרייז')
            ].map((name, i) => (
              <div key={i} className="text-slate-400 font-semibold text-lg tracking-wide">{name}</div>
            ))}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* FEATURES                                                    */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-1.5 mb-4">
              <Zap className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-xs text-slate-400">{tx('Powerful Features', 'תכונות עוצמתיות')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{tx('Every tool you need — from draft to signed', 'כל כלי שאתה צריך — מטיוטה עד חתימה')}</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {tx('Stop juggling Word docs, email threads, and e-sign tools. Quor replaces all of them in one focused platform.', 'הפסק לנהל מסמכי Word, שרשורי אימייל וכלי חתימה נפרדים. Quor מחליף את כולם בפלטפורמה אחת ממוקדת.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, i) => (
              <div
                key={i}
                className="group relative bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:bg-white/[0.06] hover:border-slate-500/20 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-slate-500/[0.02] to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="w-12 h-12 bg-accent-50 rounded-xl flex items-center justify-center text-slate-400 mb-4 group-hover:bg-accent-100 transition-all">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* FEATURE SHOWCASE — AI Proposal                              */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 bg-slate-500/10 border border-slate-500/20 rounded-full px-4 py-1.5 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-xs text-slate-300">{tx('AI-Powered', 'מונע AI')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight">
                {tx('Describe your project.', 'תאר את הפרויקט שלך.')}<br />{tx('Get a complete proposal.', 'קבל הצעה מלאה.')}
              </h2>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                {tx('Just type what you need — our AI creates a full proposal with sections, pricing, terms, and professional language. Edit anything with the block editor.', 'פשוט הקלד מה אתה צריך — הבינה המלאכותית שלנו יוצרת הצעה מלאה עם סעיפים, תמחור, תנאים ושפה מקצועית. ערוך כל דבר עם עורך הבלוקים.')}
              </p>
              <div className="space-y-4">
                {[
                  tx('Complete proposal in under 30 seconds', 'הצעה מלאה תוך פחות מ-30 שניות'),
                  tx('Industry-specific language & pricing', 'שפה ותמחור ספציפיים לתעשייה'),
                  tx('Fully editable with block editor', 'ניתן לעריכה מלאה עם עורך הבלוקים')
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-slate-500/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-slate-400" />
                    </div>
                    <span className="text-slate-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-600 mt-6">
                {tx('AI-generated content is not legal advice and does not replace a licensed attorney. All content is the user\'s sole responsibility.', 'תוכן שנוצר על ידי AI אינו ייעוץ משפטי ואינו מחליף עורך דין מורשה. כל התוכן הוא באחריות המשתמש בלבד.')}
              </p>
            </div>

            {/* Visual mockup — AI Chat */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-slate-500/10 to-accent-500/5 rounded-3xl blur-xl" />
              <div className="relative bg-[#12121a] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl">
                <div className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-white">{tx('AI Proposal Generator', 'מחולל הצעות AI')}</span>
                </div>
                <div className="p-5 space-y-4">
                  {/* User message */}
                  <div className="flex justify-end">
                    <div className="bg-accent-500 text-white text-sm px-4 py-3 rounded-2xl rounded-br-md max-w-[80%]">
                      {tx('Create a proposal for building an e-commerce website for a fashion brand', 'צור הצעה לבניית אתר מסחר אלקטרוני למותג אופנה')}
                    </div>
                  </div>
                  {/* AI response */}
                  <div className="flex justify-start">
                    <div className="bg-white/[0.06] border border-white/[0.08] text-sm px-4 py-3 rounded-2xl rounded-bl-md max-w-[85%]">
                      <div className="text-white font-medium mb-2">{tx('Generated proposal with 8 sections:', 'הצעה שנוצרה עם 8 סעיפים:')}</div>
                      <div className="space-y-1.5 text-slate-400 text-xs">
                        <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> {tx('Executive Summary', 'תקציר מנהלים')}</div>
                        <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> {tx('Project Scope & Deliverables', 'היקף הפרויקט ותוצרים')}</div>
                        <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> {tx('Technical Architecture', 'ארכיטקטורה טכנית')}</div>
                        <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> {tx('Timeline & Milestones', 'לוח זמנים ואבני דרך')}</div>
                        <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> {tx('Pricing — $18,500', 'תמחור — $18,500')}</div>
                        <div className="flex items-center gap-2"><Check className="w-3 h-3 text-emerald-400" /> {tx('Terms & Conditions', 'תנאים והגבלות')}</div>
                      </div>
                      <button className="mt-3 text-xs text-slate-400 font-medium hover:text-slate-300 flex items-center gap-1">
                        {tx('Open in Editor', 'פתח בעורך')} <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* FEATURE SHOWCASE — Editor + Signature                       */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Visual mockup — Editor */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-br from-accent-500/10 to-orange-500/5 rounded-3xl blur-xl" />
              <div className="relative bg-[#12121a] border border-white/[0.08] rounded-2xl overflow-hidden shadow-2xl">
                <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-accent-400" />
                    <span className="text-sm font-medium text-white">{tx('Block Editor', 'עורך בלוקים')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-400" />
                    <span className="text-[10px] text-slate-500">{tx('Auto-saved', 'נשמר אוטומטית')}</span>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  {/* Block items */}
                  {[
                    { type: tx('Header', 'כותרת'), content: tx('Website Redesign Proposal', 'הצעה לעיצוב מחדש של אתר'), icon: '' },
                    { type: tx('Text', 'טקסט'), content: tx('We\'re excited to present our approach to redesigning your online presence...', 'אנחנו נרגשים להציג את הגישה שלנו לעיצוב מחדש של הנוכחות המקוונת שלך...'), icon: '' },
                    { type: tx('Price Table', 'טבלת מחירים'), content: tx('3 items — Total: $18,500', '3 פריטים — סה"כ: $18,500'), icon: '' },
                    { type: tx('Timeline', 'לוח זמנים'), content: tx('4 phases — 12 weeks', '4 שלבים — 12 שבועות'), icon: '' },
                    { type: tx('Signature', 'חתימה'), content: tx('Digital signature block', 'בלוק חתימה דיגיטלית'), icon: '' },
                  ].map((block, i) => (
                    <div key={i} className="group bg-white/[0.04] border border-white/[0.06] rounded-xl px-4 py-3 hover:border-rose-500/20 transition-all cursor-pointer flex items-center gap-3">
                      <span className="text-lg flex-shrink-0">{block.icon}</span>
                      <div className="min-w-0">
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{block.type}</div>
                        <div className="text-sm text-white/80 truncate">{block.content}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-rose-500/10 border border-rose-500/20 rounded-full px-4 py-1.5 mb-6">
                <Palette className="w-3.5 h-3.5 text-rose-400" />
                <span className="text-xs text-rose-300">{tx('Visual Editor', 'עורך ויזואלי')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight">
                {tx('Drag, drop, and', 'גרור, שחרר,')}
                <br />{tx('make it yours.', 'והפוך אותו לשלך.')}
              </h2>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                {tx('22+ block types let you build exactly the proposal you need. Price tables, images, galleries, timelines — arrange them however you want.', 'יותר מ-22 סוגי בלוקים מאפשרים לך לבנות בדיוק את ההצעה שאתה צריך. טבלאות מחירים, תמונות, גלריות, לוחות זמנים — סדר אותם איך שאתה רוצה.')}
              </p>
              <div className="space-y-4">
                {[
                  tx('22+ drag-and-drop block types', 'יותר מ-22 סוגי בלוקים בגרירה ושחרור'),
                  tx('Custom branding & colors', 'מיתוג וצבעים מותאמים אישית'),
                  tx('Digital signature block built-in', 'בלוק חתימה דיגיטלית מובנה')
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-rose-400" />
                    </div>
                    <span className="text-slate-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* HOW IT WORKS                                                */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{tx('Three steps to a signed deal', 'שלושה צעדים לעסקה חתומה')}</h2>
            <p className="text-slate-400 text-lg">{tx('From idea to signature in minutes, not hours.', 'מרעיון לחתימה תוך דקות, לא שעות.')}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto relative">
            {/* Connecting line */}
            <div className="hidden sm:block absolute top-12 left-[20%] right-[20%] h-px bg-gradient-to-r from-slate-500/30 via-accent-500/30 to-orange-400/30" />

            {steps.map((step, i) => (
              <div key={i} className="text-center relative">
                <div className={`w-24 h-24 bg-gradient-to-br ${step.color} rounded-3xl flex items-center justify-center text-white mx-auto mb-6 shadow-xl shadow-slate-500/10 rotate-3 hover:rotate-0 transition-transform duration-300`}>
                  {step.icon}
                </div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">{tx('Step', 'שלב')} {i + 1}</div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* TESTIMONIALS                                                */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{tx('Real results from real businesses', 'תוצאות אמיתיות מעסקים אמיתיים')}</h2>
            <p className="text-slate-400 text-lg">{tx('Join 1,200+ freelancers and agencies who replaced their old proposal workflow with Quor.', 'הצטרף ליותר מ-1,200 פרילנסרים וסוכנויות שהחליפו את תהליך ההצעות הישן שלהם ב-Quor.')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 hover:border-slate-500/20 transition-all">
                <div className="flex items-center gap-0.5 mb-4">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center text-white font-bold text-sm">
                    {t.avatar}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* PRICING                                                     */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{tx('Pricing that pays for itself', 'תמחור שמשלם על עצמו')}</h2>
            <p className="text-slate-400 text-lg">{tx('One closed deal covers a year of Quor. Start free — no credit card needed.', 'עסקה אחת שנסגרה מכסה שנה של Quor. התחל בחינם — ללא כרטיס אשראי.')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-3xl p-8 hover:border-white/[0.12] transition-all">
              <h3 className="text-xl font-bold text-white mb-1">{tx('Free', 'חינם')}</h3>
              <p className="text-slate-500 text-sm mb-6">{tx('Try it risk-free — no card needed', 'נסה ללא סיכון — ללא כרטיס')}</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-white">$0</span>
                <span className="text-slate-500 ml-1">/{tx('mo', 'חודש')}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  tx('Up to 3 proposals', 'עד 3 הצעות'),
                  tx('Block editor', 'עורך בלוקים'),
                  tx('Digital signatures', 'חתימות דיגיטליות'),
                  tx('Link sharing', 'שיתוף קישורים'),
                  tx('Client management', 'ניהול לקוחות')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-400 text-sm">
                    <Check className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/login?signup=1')}
                className="w-full py-3.5 rounded-xl font-medium border border-white/10 text-white hover:bg-white/[0.04] transition-all"
              >
                {tx('Start Free — No Card', 'התחל בחינם — ללא כרטיס')}
              </button>
            </div>

            {/* Pro — Highlighted */}
            <div className="relative bg-gradient-to-b from-slate-500/[0.08] to-transparent border-2 border-slate-500/30 rounded-3xl p-8">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-accent-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg shadow-accent-500/30">
                {tx('Most Popular', 'הכי פופולרי')}
              </div>
              <h3 className="text-xl font-bold text-white mb-1">{tx('Pro', 'פרו')}</h3>
              <p className="text-slate-500 text-sm mb-6">{tx('For freelancers serious about closing deals', 'לפרילנסרים שרציניים לגבי סגירת עסקאות')}</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-white">$12</span>
                <span className="text-slate-500 ml-1">/{tx('mo', 'חודש')}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  tx('Up to 20 proposals/mo', 'עד 20 הצעות לחודש'),
                  tx('AI-powered creation', 'יצירה מונעת AI'),
                  tx('PDF export', 'ייצוא PDF'),
                  tx('Template library', 'ספריית תבניות'),
                  tx('Advanced analytics', 'אנליטיקה מתקדמת'),
                  tx('Email notifications', 'התראות אימייל')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/login?signup=1')}
                className="w-full py-3.5 rounded-xl font-semibold bg-accent-500 text-white hover:bg-accent-600 hover:shadow-lg hover:shadow-accent-500/25 transition-all"
              >
                {tx('Start Closing More Deals', 'התחל לסגור יותר עסקאות')}
              </button>
            </div>

            {/* Business */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-3xl p-8 hover:border-white/[0.12] transition-all">
              <h3 className="text-xl font-bold text-white mb-1">{tx('Business', 'עסקי')}</h3>
              <p className="text-slate-500 text-sm mb-6">{tx('For agencies scaling their proposal volume', 'לסוכנויות שמגדילות את נפח ההצעות')}</p>
              <div className="mb-8">
                <span className="text-5xl font-bold text-white">$39</span>
                <span className="text-slate-500 ml-1">/{tx('mo', 'חודש')}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  tx('Unlimited proposals', 'הצעות ללא הגבלה'),
                  tx('Everything in Pro', 'הכל ב-Pro'),
                  tx('Priority support', 'תמיכה עדיפה'),
                  tx('Custom branding', 'מיתוג מותאם אישית'),
                  tx('Team collaboration', 'שיתוף פעולה צוותי'),
                  tx('Advanced integrations', 'אינטגרציות מתקדמות')
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-400 text-sm">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/login?signup=1')}
                className="w-full py-3.5 rounded-xl font-medium border border-white/10 text-white hover:bg-white/[0.04] transition-all"
              >
                {tx('Scale My Proposals', 'הגדל את נפח ההצעות שלי')}
              </button>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* TRUST BADGES                                                */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 max-w-3xl mx-auto">
            {[
              { icon: <Shield className="w-5 h-5" />, label: tx('Secure & Encrypted', 'מאובטח ומוצפן') },
              { icon: <Clock className="w-5 h-5" />, label: tx('Setup in 2 Minutes', 'הגדרה ב-2 דקות') },
              { icon: <Globe className="w-5 h-5" />, label: tx('Works Worldwide', 'עובד בכל העולם') },
              { icon: <Zap className="w-5 h-5" />, label: tx('Cancel Anytime', 'ביטול בכל עת') },
            ].map((badge, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 bg-white/[0.04] border border-white/[0.06] rounded-xl flex items-center justify-center text-slate-400 mx-auto mb-2">
                  {badge.icon}
                </div>
                <span className="text-xs text-slate-500">{badge.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* FAQ                                                         */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{tx('Frequently asked questions', 'שאלות נפוצות')}</h2>
            <p className="text-slate-400">{tx('Everything you need to know about Quor.', 'כל מה שצריך לדעת על Quor.')}</p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* FINAL CTA                                                   */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="relative overflow-hidden rounded-3xl">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-accent-600 to-slate-800" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.15),_transparent_60%)]" />

            <div className="relative px-8 sm:px-16 py-16 sm:py-20 text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                {tx('Your next client is waiting for a proposal.', 'הלקוח הבא שלך מחכה להצעה.')}
              </h2>
              <p className="text-slate-100/80 text-lg mb-10 max-w-xl mx-auto">
                {tx('Stop losing deals to slow, unprofessional proposals. Join 1,200+ businesses who close faster with Quor.', 'הפסק לאבד עסקאות בגלל הצעות איטיות ולא מקצועיות. הצטרף ליותר מ-1,200 עסקים שסוגרים מהר יותר עם Quor.')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => navigate('/login?signup=1')}
                  className="group flex items-center gap-2 bg-white text-slate-700 font-bold px-10 py-4 rounded-full text-lg hover:bg-slate-50 transition-all shadow-xl shadow-black/10 hover:-translate-y-0.5"
                >
                  <span>{tx('Send My First Proposal Free', 'שלח את ההצעה הראשונה שלי בחינם')}</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
              <p className="text-slate-200/50 text-sm mt-4">{tx('No credit card. 3 proposals free forever. Setup takes 2 minutes.', 'ללא כרטיס אשראי. 3 הצעות בחינם לתמיד. הגדרה לוקחת 2 דקות.')}</p>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════ */}
        {/* FOOTER                                                      */}
        {/* ═══════════════════════════════════════════════════════════ */}
        <footer className="border-t border-white/[0.06] py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-8 mb-10">
              {/* Brand */}
              <div className="sm:col-span-2">
                <img src="/media/quor.png" alt="QUOR - Digital Signatures Made Simple" className="h-12 mb-4" />
                <p className="text-slate-500 text-sm leading-relaxed max-w-sm">
                  {tx('The modern platform for creating professional proposals, collecting digital signatures, and closing deals faster.', 'הפלטפורמה המודרנית ליצירת הצעות מקצועיות, איסוף חתימות דיגיטליות וסגירת עסקאות מהר יותר.')}
                </p>
              </div>

              {/* Product */}
              <div>
                <h4 className="text-white font-medium text-sm mb-4">{tx('Product', 'מוצר')}</h4>
                <div className="space-y-2.5">
                  <a href="#features" className="block text-sm text-slate-500 hover:text-white transition-colors">{tx('Features', 'תכונות')}</a>
                  <a href="#pricing" className="block text-sm text-slate-500 hover:text-white transition-colors">{tx('Pricing', 'מחירים')}</a>
                  <a href="/templates" className="block text-sm text-slate-500 hover:text-white transition-colors">{tx('Templates', 'תבניות')}</a>
                  <a href="#faq" className="block text-sm text-slate-500 hover:text-white transition-colors">{tx('FAQ', 'שאלות נפוצות')}</a>
                </div>
              </div>

              {/* Resources */}
              <div>
                <h4 className="text-white font-medium text-sm mb-4">Resources</h4>
                <div className="space-y-2.5">
                  <a href="/blog/how-to-write-a-proposal" className="block text-sm text-slate-500 hover:text-white transition-colors">How to Write a Proposal</a>
                  <a href="/blog/digital-signatures" className="block text-sm text-slate-500 hover:text-white transition-colors">Digital Signatures Guide</a>
                  <a href="/templates" className="block text-sm text-slate-500 hover:text-white transition-colors">Free Templates</a>
                </div>
              </div>

              {/* Legal */}
              <div>
                <h4 className="text-white font-medium text-sm mb-4">{tx('Legal', 'משפטי')}</h4>
                <div className="space-y-2.5">
                  <a href="/privacy" className="block text-sm text-slate-500 hover:text-white transition-colors">{tx('Privacy Policy', 'מדיניות פרטיות')}</a>
                  <a href="/terms" className="block text-sm text-slate-500 hover:text-white transition-colors">{tx('Terms of Service', 'תנאי שימוש')}</a>
                  <a href="/accessibility" className="block text-sm text-slate-500 hover:text-white transition-colors">{tx('Accessibility', 'נגישות')}</a>
                </div>
              </div>
            </div>

            <div className="border-t border-white/[0.06] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-slate-600">
                &copy; {new Date().getFullYear()} Quor. {tx('All rights reserved.', 'כל הזכויות שמורות.')}
              </div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                ))}
                <span className="text-xs text-slate-500 ml-2">{tx('Rated 4.9/5 by 1,200+ users', 'דירוג 4.9/5 על ידי יותר מ-1,200 משתמשים')}</span>
              </div>
            </div>
            <p className="text-center text-[11px] text-slate-700 mt-4">
              {tx('Quor is a convenience tool only — not a legal service. AI-generated content is not legal advice. Digital signatures may not be legally enforceable in all jurisdictions. Consult a licensed attorney for legal matters.', 'Quor הוא כלי נוחות בלבד — לא שירות משפטי. תוכן שנוצר על ידי AI אינו ייעוץ משפטי. חתימות דיגיטליות עשויות שלא להיות אכיפות משפטית בכל השיפוטים. התייעץ עם עורך דין מורשה בענייניים משפטיים.')}
            </p>
          </div>
        </footer>
      </div>

      {/* ─── Global keyframes ─── */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  )
}
